const ANALYTICS_STORAGE_KEY = 'equipment-action-portal.analytics.rows';

const normalizeAnalyticsRow = (row, index = 0) => {
  if (!row || typeof row !== 'object') return null;

  return {
    id: String(row.id ?? row._id ?? `analytics-row-${index}`),
    lastEntryAt: row.lastEntryAt ?? row.updatedAt ?? row.createdAt ?? null,
    userId: row.userId ?? '',
    userEmail: row.userEmail ?? '',
    empNumber: row.empNumber ?? row.employeeId ?? row.employeeNumber ?? '',
    equipmentNumber: row.equipmentNumber ?? '',
    siteId: row.siteId ?? '',
    serviceArea: row.serviceArea ?? '',
    serviceAction: row.serviceAction ?? '',
    comment: row.comment ?? '',
    outcome: row.outcome ?? '',
    arrivedAt: row.arrivedAt ?? null,
    questionAnswers: Array.isArray(row.questionAnswers) ? row.questionAnswers : [],
    cancellationReason: row.cancellationReason ?? '',
    cancellationReasonDetails: row.cancellationReasonDetails ?? '',
    qaEngagement: row.qaEngagement ?? null,
  };
};

/**
 * Normalize analytics rows from varied API payload shapes.
 * @param {unknown} rows - Analytics rows or wrapped payload.
 * @returns {Array<object>} Normalized rows.
 */
export const normalizeAnalyticsRows = (rows) => {
  let candidateRows = [];
  const payload =
    rows && typeof rows === 'object' ? /** @type {{rows?: unknown, items?: unknown, data?: unknown}} */ (rows) : null;

  if (Array.isArray(rows)) {
    candidateRows = rows;
  } else if (Array.isArray(payload?.rows)) {
    candidateRows = payload.rows;
  } else if (Array.isArray(payload?.items)) {
    candidateRows = payload.items;
  } else if (Array.isArray(payload?.data)) {
    candidateRows = payload.data;
  }

  return candidateRows.map((row, index) => normalizeAnalyticsRow(row, index)).filter(Boolean);
};

const safeParseRows = (rawRows) => {
  if (!rawRows) return [];

  try {
    return JSON.parse(rawRows);
  } catch {
    return [];
  }
};

/**
 * Get analytics rows from local storage.
 * @returns {Array<object>} Persisted and normalized analytics rows.
 */
export const getAnalyticsRows = () => {
  if (typeof window === 'undefined' || !window.localStorage) return [];
  return normalizeAnalyticsRows(safeParseRows(window.localStorage.getItem(ANALYTICS_STORAGE_KEY)));
};

/**
 * Add a new analytics row to local storage.
 * @param {object} row - Analytics row payload.
 * @returns {void}
 */
export const addAnalyticsRow = (row) => {
  if (typeof window === 'undefined' || !window.localStorage) return;

  const existingRows = getAnalyticsRows();
  const nextRows = [normalizeAnalyticsRow(row), ...existingRows].filter(Boolean);
  window.localStorage.setItem(ANALYTICS_STORAGE_KEY, JSON.stringify(nextRows));
};

/**
 * Update an existing analytics row or insert it when not present.
 * @param {object} row - Analytics row payload.
 * @returns {void}
 */
export const upsertAnalyticsRow = (row) => {
  if (typeof window === 'undefined' || !window.localStorage) return;

  const existingRows = getAnalyticsRows();
  const normalizedRow = normalizeAnalyticsRow(row);
  if (!normalizedRow) return;

  const index = existingRows.findIndex((existingRow) => existingRow.id === normalizedRow.id);

  if (index === -1) {
    window.localStorage.setItem(ANALYTICS_STORAGE_KEY, JSON.stringify([normalizedRow, ...existingRows]));
    return;
  }

  const nextRows = [...existingRows];
  nextRows[index] = { ...nextRows[index], ...normalizedRow };
  window.localStorage.setItem(ANALYTICS_STORAGE_KEY, JSON.stringify(nextRows));
};

export const analyticsStorageKey = ANALYTICS_STORAGE_KEY;
